/**
 * SDMS Import Files — the PICTURES an instrument workbook carries (PFAS: one chromatogram per
 * sample sheet), placed onto the batch's per-sample sheets exactly as legacy ALPACA does and
 * persisted into the saved .xlsx.
 *
 * OG (SpreadsheetEntryController `BindImagestoSheet` :25957) copies the uploaded sheet's pictures
 * onto the worksheet named by the sample's SysSampleCode, stacking them below the used range:
 *   • shapes are taken in order of their bottom row;
 *   • the FIRST shape on a PFAS template keeps its own column and lands at source row + 2
 *     (other templates: at bottomRow = last used row + 3);
 *   • a shape on a NEW source row starts a new band — on PFAS the first time at
 *     last used row + 3, afterwards bottomRow += height(pt)/72 + 5 — and the two label
 *     cells that sit above it in the source (`{col}{row-1}` and `B{row-1}`) are written to
 *     A/B of the row above the band;
 *   • a shape on the SAME source row goes beside the previous one (two columns past the
 *     right-most picture already on that band) when it starts in the same column, else in
 *     its own column.
 *
 * The saved workbook is never re-serialised (see sdmsXlsxPatch.ts — DevExpress rejects
 * ExcelJS output), so pictures are ADDED at the zip level: a drawing part per sheet (or the
 * sheet's existing one — the template chart lives there), its relationships, the media
 * files and the content types. `readSheetPictures` reads them back on reopen.
 */
import JSZip from 'jszip';
import type { SdmsImportPicture } from './qcBatch.api';

const EMU_PER_PX = 9525;

/** A picture placed on a batch sheet (0-based row/col of its top-left cell). */
export interface PlacedImage {
  sheetName: string;
  row: number;
  col: number;
  widthPx: number;
  heightPx: number;
  mime: string;
  base64: string;
}

/** A label cell OG writes above a picture band (0-based). */
export interface LabelWrite { sheetName: string; row: number; col: number; text: string }

export function colLetter(col0: number): string {
  let s = ''; let n = col0 + 1;
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

/**
 * OG BindImagestoSheet's placement for ONE sample sheet.
 * @param pictures  the upload sheet's pictures (source anchors, 0-based)
 * @param usedBottomRow0  the sample sheet's last used row (0-based), i.e. DevExpress
 *                        `GetUsedRange().BottomRowIndex`
 * @param pfasTemplate    TemplateName "PFAS 533" / "PFAS" (the first-shape + first-band rules)
 */
export function placeImportedPictures(
  sheetName: string, pictures: readonly SdmsImportPicture[], usedBottomRow0: number, pfasTemplate: boolean,
): { images: PlacedImage[]; labels: LabelWrite[] } {
  const images: PlacedImage[] = [];
  const labels: LabelWrite[] = [];
  const ordered = [...pictures].sort((a, b) => a.row2 - b.row2);
  let bottomRow = usedBottomRow0 + 3;          // 1-based row number, as OG's `bottomRow`
  let shapeLine = 0;                           // 1-based source top row of the current band
  let picHeightPt = 0;
  let columnIndex = -1;
  let first = 0;
  // (0-based row, 0-based right column) of every picture placed so far — the "same band" lookup.
  const placedRight: Array<{ row: number; right: number }> = [];

  for (const p of ordered) {
    const topRow1 = p.row1 + 1;                // A1 digits of TopLeftCell
    const heightPt = p.heightPx * 0.75;
    let leftCol = p.col1;
    let leftRow1: number;                      // 1-based

    if (shapeLine === 0) {
      shapeLine = topRow1; picHeightPt = heightPt; columnIndex = -1;
      leftRow1 = pfasTemplate ? p.row1 + 2 : bottomRow;
    } else if (shapeLine !== topRow1) {
      if (pfasTemplate && first === 0) { bottomRow = usedBottomRow0 + 3; first++; }
      else bottomRow += Math.trunc(picHeightPt) / 72 + 5;
      bottomRow = Math.trunc(bottomRow);
      shapeLine = topRow1;
      if (p.labelA) labels.push({ sheetName, row: bottomRow - 2, col: 0, text: p.labelA });
      if (p.labelB) labels.push({ sheetName, row: bottomRow - 2, col: 1, text: p.labelB });
      picHeightPt = heightPt; columnIndex = p.col2;
      leftRow1 = bottomRow;
    } else {
      if (p.col1 === columnIndex) {
        const onBand = placedRight.filter(x => x.row === bottomRow - 1);
        const maxRight = onBand.length ? Math.max(...onBand.map(x => x.right)) : p.col1;
        leftCol = maxRight + 2;
      }
      columnIndex = p.col2;
      leftRow1 = bottomRow;
    }

    const row0 = leftRow1 - 1;
    images.push({ sheetName, row: row0, col: leftCol, widthPx: p.widthPx, heightPx: p.heightPx, mime: p.mime, base64: p.base64 });
    placedRight.push({ row: row0, right: leftCol + Math.max(0, p.col2 - p.col1) });
  }
  return { images, labels };
}

// ── .xlsx pictures: read back on reopen ─────────────────────────────────────────

export interface SheetPicture {
  sheetName: string;
  row: number; col: number;            // 0-based top-left anchor
  rowOffPx: number; colOffPx: number;
  widthPx: number; heightPx: number;   // from a oneCellAnchor's ext, or the twoCellAnchor's span is unknown (0)
  mime: string;
  base64: string;
}

const mimeOf = (path: string): string => {
  const ext = path.toLowerCase().replace(/^.*\./, '');
  return ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : ext === 'gif' ? 'image/gif' : ext === 'bmp' ? 'image/bmp' : 'image/png';
};
const extOf = (mime: string): string => mime === 'image/jpeg' ? 'jpeg' : mime === 'image/gif' ? 'gif' : mime === 'image/bmp' ? 'bmp' : 'png';
const num = (xml: string, tag: string): number => {
  const m = xml.match(new RegExp(`<xdr:${tag}>\\s*(-?\\d+)\\s*</xdr:${tag}>`));
  return m ? Number(m[1]) : 0;
};
const relTarget = (rels: string, id: string): string | null => {
  const m = rels.match(new RegExp(`<Relationship\\b[^>]*Id="${id}"[^>]*Target="([^"]+)"`))
    ?? rels.match(new RegExp(`<Relationship\\b[^>]*Target="([^"]+)"[^>]*Id="${id}"`));
  return m?.[1] ?? null;
};
const resolvePath = (target: string, baseDir: string): string =>
  new URL(target, 'file:///' + baseDir + '/').pathname.replace(/^\//, '');

/** Sheet NAME → worksheet part path, in workbook order. */
async function sheetParts(zip: JSZip): Promise<Array<{ name: string; path: string }>> {
  const out: Array<{ name: string; path: string }> = [];
  const wbXml = await zip.file('xl/workbook.xml')?.async('string');
  const wbRels = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
  if (!wbXml || !wbRels) return out;
  for (const m of wbXml.matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const attrs = m[1] ?? '';
    const name = /name="([^"]*)"/.exec(attrs)?.[1];
    const rid = /r:id="([^"]+)"/.exec(attrs)?.[1];
    if (!name || !rid) continue;
    const t = relTarget(wbRels, rid);
    if (!t) continue;
    out.push({ name: unescapeXml(name), path: t.startsWith('/') ? t.slice(1) : resolvePath(t, 'xl') });
  }
  return out;
}

const unescapeXml = (s: string): string =>
  s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');
const escapeXml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Every embedded picture in the workbook with its anchor and bytes. Never throws. */
export async function readSheetPictures(buf: ArrayBuffer): Promise<SheetPicture[]> {
  const out: SheetPicture[] = [];
  try {
    const zip = await JSZip.loadAsync(buf);
    for (const { name, path } of await sheetParts(zip)) {
      const sheetXml = await zip.file(path)?.async('string');
      if (!sheetXml) continue;
      const drawingId = sheetXml.match(/<drawing\b[^>]*r:id="([^"]+)"/)?.[1];
      if (!drawingId) continue;
      const base = path.replace(/\/[^/]+$/, '');
      const file = path.replace(/^.*\//, '');
      const sheetRels = (await zip.file(`${base}/_rels/${file}.rels`)?.async('string')) ?? '';
      const dTarget = relTarget(sheetRels, drawingId);
      if (!dTarget) continue;
      const drawingPath = resolvePath(dTarget, base);
      const dXml = await zip.file(drawingPath)?.async('string');
      if (!dXml) continue;
      const dBase = drawingPath.replace(/\/[^/]+$/, '');
      const dFile = drawingPath.replace(/^.*\//, '');
      const dRels = (await zip.file(`${dBase}/_rels/${dFile}.rels`)?.async('string')) ?? '';

      for (const m of dXml.matchAll(/<xdr:(oneCellAnchor|twoCellAnchor|absoluteAnchor)\b[\s\S]*?<\/xdr:\1>/g)) {
        const block = m[0];
        if (!/<xdr:pic\b/.test(block)) continue;
        const embed = block.match(/<a:blip\b[^>]*r:embed="([^"]+)"/)?.[1];
        if (!embed) continue;
        const mTarget = relTarget(dRels, embed);
        if (!mTarget) continue;
        const mediaPath = resolvePath(mTarget, dBase);
        const bytes = await zip.file(mediaPath)?.async('base64');
        if (!bytes) continue;
        const from = block.match(/<xdr:from>([\s\S]*?)<\/xdr:from>/)?.[1] ?? '';
        const ext = block.match(/<xdr:ext\b[^>]*cx="(\d+)"[^>]*cy="(\d+)"/);
        const a = block.match(/<a:ext\b[^>]*cx="(\d+)"[^>]*cy="(\d+)"/);
        const cx = Number(ext?.[1] ?? a?.[1] ?? 0), cy = Number(ext?.[2] ?? a?.[2] ?? 0);
        out.push({
          sheetName: name,
          row: num(from, 'row'), col: num(from, 'col'),
          rowOffPx: num(from, 'rowOff') / EMU_PER_PX, colOffPx: num(from, 'colOff') / EMU_PER_PX,
          widthPx: Math.round(cx / EMU_PER_PX), heightPx: Math.round(cy / EMU_PER_PX),
          mime: mimeOf(mediaPath), base64: bytes,
        });
      }
    }
  } catch {
    return out;
  }
  return out;
}

// ── .xlsx pictures: add at the zip level (surgical, like sdmsXlsxPatch) ──────────

const DRAWING_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing';
const IMAGE_REL   = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/image';
const DRAWING_CT  = 'application/vnd.openxmlformats-officedocument.drawing+xml';

const nextId = (xml: string, prefix: string): string => {
  let n = 1;
  for (const m of xml.matchAll(new RegExp(`Id="${prefix}(\\d+)"`, 'g'))) n = Math.max(n, Number(m[1]) + 1);
  return `${prefix}${n}`;
};
const nextPartIndex = (zip: JSZip, dir: string, stem: string): number => {
  let n = 1;
  zip.forEach(p => { const m = p.match(new RegExp(`^${dir}/${stem}(\\d+)\\.`)); if (m) n = Math.max(n, Number(m[1]) + 1); });
  return n;
};

function picXml(id: number, relId: string, name: string, row: number, col: number, wPx: number, hPx: number): string {
  const cx = Math.max(1, Math.round(wPx * EMU_PER_PX)), cy = Math.max(1, Math.round(hPx * EMU_PER_PX));
  return `<xdr:oneCellAnchor><xdr:from><xdr:col>${col}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${row}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>`
    + `<xdr:ext cx="${cx}" cy="${cy}"/>`
    + `<xdr:pic><xdr:nvPicPr><xdr:cNvPr id="${id}" name="${escapeXml(name)}"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr>`
    + `<xdr:blipFill><a:blip xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="${relId}"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill>`
    + `<xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic>`
    + `<xdr:clientData/></xdr:oneCellAnchor>`;
}

/**
 * Add the placed pictures to the workbook WITHOUT re-serialising it: media parts, drawing
 * part(s), relationships, `<drawing>` element and content types only. Returns the original
 * buffer untouched when there is nothing to add. Never throws — a failure keeps the cell
 * edits (the pictures are decoration).
 */
export async function patchWorkbookPictures(originalBuf: ArrayBuffer, images: readonly PlacedImage[]): Promise<ArrayBuffer> {
  if (!images.length) return originalBuf;
  try {
    const zip = await JSZip.loadAsync(originalBuf);
    const parts = await sheetParts(zip);
    let ct = (await zip.file('[Content_Types].xml')?.async('string')) ?? '';
    if (!ct) return originalBuf;
    let added = 0;
    const bySheet = new Map<string, PlacedImage[]>();
    for (const im of images) (bySheet.get(im.sheetName) ?? bySheet.set(im.sheetName, []).get(im.sheetName)!).push(im);

    for (const [sheetName, list] of bySheet) {
      const part = parts.find(p => p.name === sheetName);
      if (!part) continue;
      let sheetXml = await zip.file(part.path)?.async('string');
      if (!sheetXml) continue;
      const base = part.path.replace(/\/[^/]+$/, '');
      const file = part.path.replace(/^.*\//, '');
      const sheetRelsPath = `${base}/_rels/${file}.rels`;
      let sheetRels = (await zip.file(sheetRelsPath)?.async('string'))
        ?? '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>';

      // The sheet's drawing part: reuse the existing one (the template chart lives there) or create it.
      let drawingPath: string;
      const existingId = sheetXml.match(/<drawing\b[^>]*r:id="([^"]+)"/)?.[1];
      const existingTarget = existingId ? relTarget(sheetRels, existingId) : null;
      if (existingTarget) {
        drawingPath = resolvePath(existingTarget, base);
      } else {
        const n = nextPartIndex(zip, 'xl/drawings', 'drawing');
        drawingPath = `xl/drawings/drawing${n}.xml`;
        const rid = nextId(sheetRels, 'rId');
        sheetRels = sheetRels.replace('</Relationships>',
          `<Relationship Id="${rid}" Type="${DRAWING_REL}" Target="../drawings/drawing${n}.xml"/></Relationships>`);
        // <drawing> precedes legacyDrawing / tableParts / extLst in CT_Worksheet.
        const tag = `<drawing xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="${rid}"/>`;
        const at = sheetXml.search(/<(legacyDrawing|legacyDrawingHF|picture|oleObjects|controls|webPublishItems|tableParts|extLst)\b/);
        sheetXml = at >= 0 ? sheetXml.slice(0, at) + tag + sheetXml.slice(at) : sheetXml.replace('</worksheet>', tag + '</worksheet>');
        zip.file(part.path, sheetXml, { createFolders: false });
        if (!ct.includes(`PartName="/${drawingPath}"`))
          ct = ct.replace('</Types>', `<Override PartName="/${drawingPath}" ContentType="${DRAWING_CT}"/></Types>`);
      }
      zip.file(sheetRelsPath, sheetRels, { createFolders: false });

      const dBase = drawingPath.replace(/\/[^/]+$/, '');
      const dFile = drawingPath.replace(/^.*\//, '');
      const dRelsPath = `${dBase}/_rels/${dFile}.rels`;
      let dXml = (await zip.file(drawingPath)?.async('string'))
        ?? '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"></xdr:wsDr>';
      let dRels = (await zip.file(dRelsPath)?.async('string'))
        ?? '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>';
      let shapeId = 1;
      for (const m of dXml.matchAll(/<xdr:cNvPr\b[^>]*id="(\d+)"/g)) shapeId = Math.max(shapeId, Number(m[1]) + 1);

      for (const im of list) {
        const ext = extOf(im.mime);
        const n = nextPartIndex(zip, 'xl/media', 'image');
        const mediaPath = `xl/media/image${n}.${ext}`;
        zip.file(mediaPath, im.base64, { base64: true, createFolders: false });
        if (!new RegExp(`<Default\\b[^>]*Extension="${ext}"`, 'i').test(ct))
          ct = ct.replace('<Types', `<Types`).replace(/(<Types\b[^>]*>)/, `$1<Default Extension="${ext}" ContentType="${im.mime}"/>`);
        const rid = nextId(dRels, 'rId');
        dRels = dRels.replace('</Relationships>', `<Relationship Id="${rid}" Type="${IMAGE_REL}" Target="../media/image${n}.${ext}"/></Relationships>`);
        dXml = dXml.replace('</xdr:wsDr>', picXml(shapeId++, rid, `SDMS import ${n}`, im.row, im.col, im.widthPx, im.heightPx) + '</xdr:wsDr>');
        added++;
      }
      zip.file(drawingPath, dXml, { createFolders: false });
      zip.file(dRelsPath, dRels, { createFolders: false });
    }
    if (!added) return originalBuf;
    zip.file('[Content_Types].xml', ct, { createFolders: false });
    return await (zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }) as Promise<ArrayBuffer>);
  } catch (e) {
    console.warn('[SDMS] picture patch failed — saving without the imported images', e);
    return originalBuf;
  }
}
